import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// ── COUNTDOWN DE RESERVA ──────────────────────────────────────
function initCartReservation() {
  const el = document.getElementById('cart-reservation-countdown');
  if (!el) return;

  const STORAGE_KEY = 'cart-reservation-end';
  const DURATION = 10 * 60 * 1000; // 10 minutos

  let endTime = parseInt(sessionStorage.getItem(STORAGE_KEY) || '0');
  if (!endTime || endTime < Date.now()) {
    endTime = Date.now() + DURATION;
    sessionStorage.setItem(STORAGE_KEY, String(endTime));
  }

  function tick() {
    const remaining = endTime - Date.now();
    if (remaining <= 0) {
      el.textContent = '0:00';
      // Renovar el timer cuando llega a 0
      endTime = Date.now() + DURATION;
      sessionStorage.setItem(STORAGE_KEY, String(endTime));
      return;
    }
    const mins = Math.floor(remaining / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    el.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  tick();
  setInterval(tick, 1000);
}

// ── UPSELLS ───────────────────────────────────────────────────
async function initCartUpsells() {
  const list = document.getElementById('cart-upsells-list');
  const wrap = document.getElementById('cart-upsells');
  if (!list || !wrap) return;

  try {
    // Obtener productos del carrito actual
    const cartRes  = await fetch('/cart.js');
    const cart     = await cartRes.json();
    if (!cart.items?.length) { wrap.style.display = 'none'; return; }

    // Upsell set in Theme settings > Upsell is server-rendered first; recommendations only fill the rest
    const fixedCards = [...list.querySelectorAll('[data-upsell-fixed]')];
    const fixedId    = Number(wrap.dataset.upsellFixedId) || 0;
    const recLimit   = Math.max(0, 2 - fixedCards.length);
    const renderRecs = (html) => {
      list.querySelectorAll(':scope > :not([data-upsell-fixed])').forEach((n) => n.remove());
      if (html) list.insertAdjacentHTML('beforeend', html);
      wrap.style.display = list.children.length ? '' : 'none';
    };
    if (!recLimit) { renderRecs(''); return; }

    // Tomar el primer producto del carrito y buscar recomendaciones
    const productId = cart.items[0].product_id;
    const recRes = await fetch(`/recommendations/products.json?product_id=${productId}&limit=2&intent=related`);
    const recData = await recRes.json();
    const products = recData.products;

    if (!products?.length) { renderRecs(''); return; }

    // Filtrar productos que ya están en el carrito
    const cartProductIds = cart.items.map(i => i.product_id);
    const filtered = products.filter(p => !cartProductIds.includes(p.id) && p.id !== fixedId).slice(0, recLimit);

    if (!filtered.length) { renderRecs(''); return; }

    renderRecs(filtered.map(p => {
      const variants = p.variants;
      const image    = p.featured_image || '';
      const price    = (p.price / 100).toLocaleString('es-EU', { style: 'currency', currency: 'EUR' });

      return `
        <div class="cart-upsell-item" data-product-id="${p.id}">
          <img class="cart-upsell-item__image" src="${image}" alt="${p.title}">
          <div class="cart-upsell-item__info">
            <p class="cart-upsell-item__title">${p.title}</p>
            <p class="cart-upsell-item__price">${price}</p>
            <div class="cart-upsell-item__select--content">
              <select class="cart-upsell-item__select" id="upsell-select-${p.id}">
                ${variants.map(v => `<option value="${v.id}" ${!v.available ? 'disabled' : ''}>${ v.title.split('/')[0]  }</option>`).join('')}
              </select>
            </div>
          </div>
          <button
            class="cart-upsell-item__btn"
            onclick="addUpsellToCart(${p.id})"
            aria-label="Agregar al carrito"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="24" viewBox="0 0 8 24" fill="none">
              <path d="M4.00016 8H3.66683C3.48273 8 3.3335 8.14924 3.3335 8.33333V15.6667C3.3335 15.8508 3.48273 16 3.66683 16H4.00016C4.18426 16 4.3335 15.8508 4.3335 15.6667V8.33333C4.3335 8.14924 4.18426 8 4.00016 8Z" fill="white"/>
              <path d="M8 12V11.6667C8 11.4826 7.85076 11.3334 7.66667 11.3334H0.333333C0.149238 11.3334 0 11.4826 0 11.6667V12C0 12.1841 0.149238 12.3334 0.333333 12.3334H7.66667C7.85076 12.3334 8 12.1841 8 12Z" fill="white"/>
            </svg>
          </button>
        </div>
      `;
    }).join(''));

  } catch (e) {
    console.error('Upsells error:', e);
    wrap.style.display = 'none';
  }
}

// Upsell cards with colour + size pickers keep a hidden [data-upsell-variants] select in sync
window.upsellSync = function(card) {
  if (!card) return;
  const v = card.querySelector('[data-upsell-variants]');
  const c = card.querySelector('[data-upsell-opt="c"]');
  const s = card.querySelector('[data-upsell-opt="s"]');
  if (!v || (!c && !s)) return;
  const opt = [...v.options].find((o) => (!c || o.dataset.c === c.value) && (!s || o.dataset.s === s.value));
  const btn = card.querySelector('.cart-upsell-item__btn, [data-upsell-add]');
  if (opt && !opt.disabled) { v.value = opt.value; if (btn) btn.disabled = false; }
  else if (btn) { btn.disabled = true; }
  const img = card.querySelector('[data-upsell-img]');
  if (img && opt && opt.dataset.img) { img.removeAttribute('srcset'); img.src = opt.dataset.img; }
};
document.addEventListener('change', (e) => {
  const sel = e.target.closest && e.target.closest('[data-upsell-opt]');
  if (sel) window.upsellSync(sel.closest('[data-upsell-card]'));
});

window.addUpsellToCart = async function(productId) {
  const select    = document.getElementById(`upsell-select-${productId}`);
  const variantId = parseInt(select?.value);
  if (!variantId) return;

  const btn = document.querySelector(`.cart-upsell-item[data-product-id="${productId}"] .cart-upsell-item__btn`);
  if (btn) { btn.style.opacity = '0.5'; btn.disabled = true; }

  try {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sectionIds.join(','), 
      }),
    });

    const data = await response.json();

    if (data.status) {
      console.error('Error adding upsell:', data.message);
      return;
    }

    const cartItemsComponent = document.querySelector('cart-items-component');

    // if (cartItemsComponent) {
    //   cartItemsComponent.dispatchEvent(
    //     new CustomEvent('cart:update', {
    //       bubbles: true,
    //       detail: {
    //         resource: data,
    //         sourceId: 'upsell',
    //         data: {
    //           source: 'upsell',
    //           sections: data.sections,
    //         },
    //       },
    //     })
    //   );
    // }

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: 'upsell',
          data: {
            source: 'upsell',
            sections: data.sections,
          },
        },
      })
    );



  } catch(e) {
    console.error('Upsell add error:', e);
  } finally {
    if (btn) { btn.style.opacity = ''; btn.disabled = false; }
  }
};

// ── INIT cuando el drawer se abre ────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return;

  // Cuando el drawer se abre
  drawer.addEventListener('dialog:open', () => {
    initCartReservation();
    initCartUpsells();
    updateUpsellTitle();
  });

  // Si ya está abierto al cargar
  if (drawer.querySelector('dialog[open]')) {
    initCartReservation();
    initCartUpsells();
    updateUpsellTitle();
  }

  // Re-init cuando Horizon actualiza el carrito (morph)
  // Horizon dispara este evento después de cada cambio de carrito
  document.addEventListener('cart:updated', () => {
    initCartUpsells();
  });

  // Respaldo: MutationObserver sobre el contenido del drawer
  // para detectar cuando Horizon reemplaza el DOM
  const dialog = drawer.querySelector('dialog');
  if (dialog) {
    const observer = new MutationObserver(() => {
      const upsellsWrap = document.getElementById('cart-upsells');
      // Si el contenedor existe pero la lista está vacía, re-inicializar
      if (upsellsWrap) {
        const list = document.getElementById('cart-upsells-list');
        if (list && list.children.length === 0) {
          initCartUpsells();
        }
      }
    });

    observer.observe(dialog, { childList: true, subtree: true });
  }
});

// ── Rotating upsell percentage ──────────────────────────────────────────────
const UPSELL_PERCENTAGES = [72, 78, 81, 84, 86, 88, 91, 93];
const UPSELL_STORAGE_KEY = 'cart_upsell_pct';
const UPSELL_TTL_MS = 24 * 60 * 60 * 1000; // 24 horas

function getUpsellPercentage() {
  try {
    const stored = localStorage.getItem(UPSELL_STORAGE_KEY);
    if (stored) {
      const { value, timestamp } = JSON.parse(stored);
      const age = Date.now() - timestamp;
      if (age < UPSELL_TTL_MS) return value;
    }
  } catch (_) {}

  // Generar nuevo porcentaje (diferente al anterior si es posible)
  let prev = null;
  try {
    const stored = localStorage.getItem(UPSELL_STORAGE_KEY);
    if (stored) prev = JSON.parse(stored).value;
  } catch (_) {}

  const candidates = UPSELL_PERCENTAGES.filter(p => p !== prev);
  const pool = candidates.length ? candidates : UPSELL_PERCENTAGES;
  const value = pool[Math.floor(Math.random() * pool.length)];

  try {
    localStorage.setItem(UPSELL_STORAGE_KEY, JSON.stringify({ value, timestamp: Date.now() }));
  } catch (_) {}

  return value;
}

function updateUpsellTitle() {
  const el = document.querySelector('.cart-drawer__upsells-title');
  if (el && el.hasAttribute('data-upsell-fixed-title')) return;
  if (el) el.textContent = `${getUpsellPercentage()}% also bought`;
}